import { useDepositForm } from "@/hooks";
import { useMemo, type ReactNode } from "react";
import { DialogAwareButtonContainer, DialogAwareContentContainer } from "../shared";
import { TokenInput } from "./TokenInput";
import { DEPOSIT_TOKEN_OPTIONS } from "@/config";
import { WalletGatedButton } from "./WalletGatedButton";
import { TOKEN_IDS } from "@/types";
import { parseUnits } from "viem";

const minimumDeposits = {
    [TOKEN_IDS.USDC]: 2e-6,
} as const;

interface DepositFormProps {
    isLoading?: boolean;
    isDialog?: boolean;
    apyContent?: (depositAmount: string) => ReactNode;
    tokenInputLabel?: string;
}

export function DepositForm({ isLoading, isDialog, apyContent, tokenInputLabel }: DepositFormProps) {
    const {
        depositAmount,
        depositAmountError,
        selectedTokenId,
        submitDeposit,
        selectTokenId,
        setMaxAmount,
        isMaxDisabled,
        isAmountOverBalance,
    } = useDepositForm();

    const minAmountError = useMemo(() => {
        if (!selectedTokenId) return;
        if (!minimumDeposits.hasOwnProperty(selectedTokenId)) return;
        if (!depositAmount.value) return;
        if (depositAmount.value === "0") {
            return;
        }

        try {
            const minDeposit = minimumDeposits[selectedTokenId as keyof typeof minimumDeposits];

            const bigDeposit = parseUnits(depositAmount.value, 18);
            const bigMinDeposit = parseUnits(minDeposit.toString(), 18);

            if (bigDeposit < bigMinDeposit) {
                return {
                    type: "min",
                    message: `Minimum ${selectedTokenId} deposit is ${minDeposit}`,
                };
            }
        } catch (error) {
            console.error("Error checking minimum deposit:", error);
        }
    }, [depositAmount.value, selectedTokenId]);

    return (
        <form onSubmit={submitDeposit}>
            <DialogAwareContentContainer isDialog={isDialog}>
                <TokenInput
                    label={tokenInputLabel}
                    amount={depositAmount}
                    amountError={depositAmountError ?? minAmountError}
                    tokenOptions={DEPOSIT_TOKEN_OPTIONS}
                    selectedTokenId={selectedTokenId}
                    handleMaxClick={setMaxAmount}
                    handleTokenSelect={selectTokenId}
                    isMaxDisabled={isMaxDisabled}
                />
                {apyContent && <div className="flex justify-center mt-4 mb-1 ">{apyContent(depositAmount.value)}</div>}
            </DialogAwareContentContainer>
            <div className="flex justify-center h-1" />
            <DialogAwareButtonContainer isDialog={isDialog}>
                <WalletGatedButton
                    size="md"
                    type="submit"
                    isLoading={isLoading}
                    fullWidth
                    variant="primary"
                    color="primary"
                    disabled={isLoading || !depositAmount.value || !selectedTokenId || isAmountOverBalance}
                >
                    Deposit
                </WalletGatedButton>
            </DialogAwareButtonContainer>
        </form>
    );
}
